import type { SignalPregleda } from '@koda/shared';
import { useState, type JSX } from 'react';

import { useBesedila } from '../lib/jezik';
import { Lupa } from './kazalniki-skupno';

interface Lastnosti {
  signali: SignalPregleda[];
  /** `false`: signalov ni mogoče zanesljivo oceniti (razlog v opombi). */
  ocenjeni: boolean;
  opomba: string | null;
  naIzracun: (kazalnik: string) => void;
}

const kljucSignala = (s: SignalPregleda): string => `${s.pravilo}|${s.predmet ?? ''}`;

/** Puščica gumba Več / Manj. */
function Sevron({ gor }: { gor: boolean }): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d={gor ? 'M3.5 8.5 L7 5 L10.5 8.5' : 'M3.5 5.5 L7 9 L10.5 5.5'} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Razdelek »Za pregled« (točka 4): vsako opozorilo je ena zaprta vrstica –
 * ikona, naslov, pod njim ključni številki (povzetek pravila iz kataloga) in
 * ime kazalnika, desno »Več«. Klik odpre razlago in »Poglej izračun«. Privzeto
 * so vse zaprte; stopenj resnosti ni. Signal ni diagnoza (6.3).
 */
export function ZaPregled({ signali, ocenjeni, opomba, naIzracun }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const [odprti, setOdprti] = useState<Set<string>>(new Set());
  const preklopi = (k: string): void => {
    setOdprti((prej) => {
      const novi = new Set(prej);
      if (novi.has(k)) novi.delete(k);
      else novi.add(k);
      return novi;
    });
  };
  return (
    <section className="kz-karta kz-za-pregled" aria-label={b.pk.zaPregled}>
      <div className="kz-za-glava">
        <h2>
          {b.pk.zaPregled}
          {ocenjeni && signali.length > 0 && (
            <span className="kz-stevilo-signalov" aria-label={b.pk.steviloSignalov(signali.length)}>
              {signali.length}
            </span>
          )}
        </h2>
        <span className="kz-za-opomba">{b.pk.zaPregledOpomba}</span>
      </div>
      {/* Neusklajeni podatki: prazen seznam ne pomeni »ni signalov«, zato
          se pokaže samo razlog, da jih ni mogoče zanesljivo oceniti. */}
      {!ocenjeni ? null : signali.length === 0 ? (
        <p className="kz-za-prazno">{b.pk.niSignalov}</p>
      ) : (
        <ul className="kz-signali">
          {signali.map((s) => {
            const k = kljucSignala(s);
            const odprt = odprti.has(k);
            return (
              <li key={k} className="kz-signal">
                <span className="kz-signal-ikona">
                  <Lupa />
                </span>
                <div className="kz-signal-vsebina">
                  <div className="kz-signal-glava">
                    <span className="kz-signal-naslov">{s.naslov}</span>
                    <span className="kz-signal-povzetek">
                      {s.povzetek !== undefined && s.povzetek !== null && `${s.povzetek} `}
                      <span className="kz-tisje">
                        {s.povzetek !== undefined && s.povzetek !== null ? '· ' : ''}
                        {s.nazivKazalnika}
                      </span>
                    </span>
                  </div>
                  {odprt && (
                    <>
                      <p className="kz-signal-besedilo">{s.besedilo}</p>
                      <button
                        type="button"
                        className="kz-povezava"
                        onClick={() => {
                          naIzracun(s.kazalnik);
                        }}
                      >
                        {b.pk.pogledIzracun}
                      </button>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="kz-vec"
                  aria-expanded={odprt}
                  aria-label={odprt ? b.pk.skrijRazlago : b.pk.pokaziRazlago}
                  onClick={() => {
                    preklopi(k);
                  }}
                >
                  <span className="kz-vec-napis">{odprt ? b.pk.manj : b.pk.vec}</span>
                  <Sevron gor={odprt} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {opomba !== null && <p className="drobno">{opomba}</p>}
    </section>
  );
}
